<script lang="ts">
  import { onMount } from 'svelte';
  import type { PreviewContent } from '$stores/preview.js';

  export let preview: PreviewContent | null = null;
  export let linkRect: DOMRect;
  export let parentRect: DOMRect | null = null;
  export let level: number = 0;
  export let onMouseEnter: ((level: number) => void) | null = null;
  export let onMouseLeave: ((level: number) => void) | null = null;
  export let onLinkHover: ((level: number, link: HTMLAnchorElement, parentEl: HTMLElement) => void) | null = null;
  export let onClickLink: (() => void) | null = null;

  let element: HTMLDivElement;
  let infoboxEl: HTMLDivElement | undefined;
  let moreEl: HTMLSpanElement | undefined;

  // Reset showInfobox when preview content changes (handles component reuse via keyed {#each})
  let lastPreviewPath: string | null = null;
  $: {
    const currentPath = preview?.path ?? null;
    if (currentPath !== lastPreviewPath) {
      lastPreviewPath = currentPath;
      showInfobox = false;
    }
  }

  function formatPath(p: PreviewContent): string {
    const segments = p.path.replace(/#.*$/, '').split('/').filter(Boolean);
    if (segments.length > 0) segments[segments.length - 1] = p.title;
    return segments.map((s, i) =>
      i < segments.length - 1
        ? s.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        : s
    ).join(' / ');
  }

  let posX = 0;
  let posY = 0;
  let maxHeight = 0;
  let infoboxX = 0;
  let infoboxY = 0;
  let visible = false;
  let showInfobox = false;
  // Which side the infobox should open: 'right' or 'left'
  let infoboxSide: 'left' | 'right' = 'right';

  const MARGIN = 12;
  const INFOBOX_GAP = 8;

  function positionInfobox() {
    if (!infoboxEl || !element) return;

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const popRect = element.getBoundingClientRect();
    const ibRect = infoboxEl.getBoundingClientRect();

    let ix: number;
    let iy = popRect.top;

    if (infoboxSide === 'left') {
      ix = popRect.left - ibRect.width - INFOBOX_GAP;
      if (ix < MARGIN) {
        ix = popRect.right + INFOBOX_GAP;
      }
    } else {
      ix = popRect.right + INFOBOX_GAP;
      if (ix + ibRect.width > vw - MARGIN) {
        ix = popRect.left - ibRect.width - INFOBOX_GAP;
      }
    }

    if (ix < MARGIN) ix = MARGIN;
    if (ix + ibRect.width > vw - MARGIN) ix = vw - MARGIN - ibRect.width;

    if (iy + ibRect.height > vh - MARGIN) {
      iy = vh - MARGIN - ibRect.height;
    }
    if (iy < MARGIN) {
      iy = MARGIN;
    }

    infoboxX = ix;
    infoboxY = iy;
  }

  onMount(() => {
    requestAnimationFrame(() => {
      if (!element) return;

      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const el = element.getBoundingClientRect();

      if (parentRect) {
        let x = parentRect.right + 8;
        let y = linkRect.top;

        if (x + el.width > vw - MARGIN) {
          x = parentRect.left - el.width - 8;
        }
        if (x < MARGIN) {
          x = MARGIN;
        }

        if (y + el.height > vh - MARGIN) {
          y = vh - MARGIN - el.height;
        }
        if (y < MARGIN) {
          y = MARGIN;
        }

        posX = x;
        posY = y;
      } else {
        let x = linkRect.left;
        let y = linkRect.bottom + 8;

        if (x + el.width > vw - MARGIN) {
          x = vw - MARGIN - el.width;
        }
        if (x < MARGIN) {
          x = MARGIN;
        }

        if (y + el.height > vh - MARGIN) {
          y = linkRect.top - el.height - 8;
        }
        if (y < MARGIN) {
          y = MARGIN;
        }

        posX = x;
        posY = y;
      }

      // Set max-height to available viewport space from final position
      maxHeight = vh - posY - MARGIN;

      // Determine which side has more space for the companion card
      if (preview?.infoboxHtml) {
        const triggerCenter = parentRect
          ? (parentRect.left + parentRect.right) / 2
          : (linkRect.left + linkRect.right) / 2;
        const popoverCenter = posX + el.width / 2;
        const popoverIsLeft = popoverCenter < triggerCenter;
        infoboxSide = popoverIsLeft ? 'left' : 'right';
      }

      visible = true;
    });
  });

  function handleMoreEnter() {
    showInfobox = true;
    // Wait for the infobox element to render, then position it
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        positionInfobox();
      });
    });
  }

  function isInsidePreviewUI(el: Element | null): boolean {
    if (!el) return false;
    return !!(el.closest('.link-preview') || el.closest('.preview-infobox-float'));
  }

  function handleMoreLeave(e: MouseEvent) {
    const related = e.relatedTarget as Element | null;
    // If moving to the infobox panel or another preview element, keep open
    if (related && (related.closest('.preview-infobox-float') || isInsidePreviewUI(related))) {
      return;
    }
    // Brief delay to allow mouse to reach the infobox
    setTimeout(() => {
      if (!infoboxEl?.matches(':hover') && !moreEl?.matches(':hover')) {
        showInfobox = false;
      }
    }, 100);
  }

  function handleInfoboxLeave(e: MouseEvent) {
    const related = e.relatedTarget as Element | null;
    // If moving to the "More" trigger or a child preview, keep open
    if (related && (related === moreEl || related.closest('.preview-more') || isInsidePreviewUI(related))) {
      return;
    }
    // Close the infobox and notify the tracking system
    showInfobox = false;
    if (onMouseLeave) onMouseLeave(level);
  }

  function handleClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const link = target.closest('a[href^="#/"]');
    if (link && onClickLink) {
      onClickLink();
    }
  }

  function handlePreviewMouseOver(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const link = target.closest('a[href^="#/"]') as HTMLAnchorElement | null;
    if (!link || !onLinkHover || !element) return;

    onLinkHover(level, link, element);
  }
</script>

<!-- svelte-ignore a11y_no_static_element_interactions a11y_mouse_events_have_key_events a11y_click_events_have_key_events -->
<div
  bind:this={element}
  class="link-preview"
  class:visible
  style="left: {posX}px; top: {posY}px;{maxHeight ? ` max-height: ${maxHeight}px;` : ''}"
  on:mouseenter={() => onMouseEnter && onMouseEnter(level)}
  on:mouseleave={(e) => {
    const related = e.relatedTarget;
    // Don't trigger leave if moving to THIS component's own infobox float
    if (related && related instanceof Element && infoboxEl && infoboxEl.contains(related)) return;
    if (onMouseLeave) onMouseLeave(level);
  }}
  on:mouseover={handlePreviewMouseOver}
  on:click={handleClick}
>
  {#if preview}
    <div class="preview-content" class:has-image-wide={preview.previewImage?.orientation === 'wide'} class:has-image-tall={preview.previewImage?.orientation === 'tall'}>
      {#if preview.previewImage?.orientation === 'wide'}
        <img class="preview-image preview-image-wide" src={preview.previewImage.src} alt={preview.previewImage.alt} />
      {/if}
      <div class="preview-body">
        {#if preview.previewImage?.orientation === 'tall'}
          <img class="preview-image preview-image-tall" src={preview.previewImage.src} alt={preview.previewImage.alt} />
        {/if}
        <div class="preview-text">
          <div class="preview-header">
            <h4 class="preview-title">{preview.title}</h4>
            {#if preview.infoboxHtml}
              <!-- svelte-ignore a11y_mouse_events_have_key_events -->
              <span
                bind:this={moreEl}
                class="preview-more"
                class:active={showInfobox}
                on:mouseenter={handleMoreEnter}
                on:mouseleave={handleMoreLeave}
              >{__STRINGS__.imageViewer.moreLabel}</span>
            {/if}
          </div>
          <div class="preview-excerpt markdown-content">{@html preview.excerptHtml}</div>
        </div>
      </div>
      <span class="preview-path">{formatPath(preview)}</span>
    </div>
  {:else}
    <div class="error">{__STRINGS__.imageViewer.previewUnavailable}</div>
  {/if}
</div>
<!-- svelte-ignore a11y_no_static_element_interactions a11y_mouse_events_have_key_events a11y_click_events_have_key_events -->
{#if preview?.infoboxHtml && showInfobox}
  <div
    bind:this={infoboxEl}
    class="preview-infobox-float markdown-content"
    class:visible
    style="left: {infoboxX}px; top: {infoboxY}px;"
    on:mouseenter={() => { onMouseEnter && onMouseEnter(level); }}
    on:mouseleave={handleInfoboxLeave}
    on:mouseover={handlePreviewMouseOver}
    on:click={handleClick}
  >
    {@html preview.infoboxHtml}
  </div>
{/if}

<style>
  .link-preview {
    position: fixed;
    z-index: 1000;
    box-sizing: border-box;
    max-width: 400px;
    min-width: 200px;
    overflow-y: auto;
    background: var(--color-surface, #fff);
    border: 1px solid var(--color-border, #e0e0e0);
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    opacity: 0;
    pointer-events: none;
  }

  .link-preview.visible {
    opacity: 1;
    pointer-events: auto;
  }

  .error {
    padding: 1rem;
    color: var(--color-text-muted, #666);
    font-size: 0.85rem;
  }

  .preview-content {
    padding: 0.75rem 1rem;
  }

  .preview-content.has-image-wide {
    padding-top: 0;
  }

  .preview-content.has-image-wide .preview-body {
    padding: 0 1rem;
  }

  .preview-content.has-image-wide .preview-path {
    padding: 0 1rem;
  }

  .preview-content.has-image-tall {
    padding-left: 0;
  }

  /* Wide image: full width above text */
  .preview-image-wide {
    display: block;
    width: calc(100% + 2rem);
    margin-left: -1rem;
    margin-right: -1rem;
    max-height: 120px;
    object-fit: cover;
    border-radius: 8px 8px 0 0;
    margin-bottom: 0.5rem;
  }

  /* Tall image: floated beside text */
  .preview-body {
    display: flex;
    gap: 0.75rem;
  }

  .preview-image-tall {
    width: 80px;
    max-height: 140px;
    object-fit: cover;
    border-radius: 8px 0 0 8px;
    flex-shrink: 0;
    align-self: stretch;
  }

  .preview-text {
    flex: 1;
    min-width: 0;
  }

  .preview-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .preview-title {
    margin: 0 0 0.5rem;
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-heading, #1a1a2e);
    line-height: 1.3;
  }

  .preview-more {
    font-size: 0.75rem;
    color: var(--color-primary, #e94560);
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
    opacity: 0.7;
    transition: opacity 0.15s;
  }

  .preview-more:hover,
  .preview-more.active {
    opacity: 1;
  }

  .preview-excerpt {
    margin: 0 0 0.5rem;
    font-size: 0.85rem;
    color: var(--color-text, #333);
    line-height: 1.5;
  }

  /* Hide interactive roll table elements in previews */
  .preview-excerpt :global(.roll-table-controls) {
    display: none;
  }

  .preview-excerpt :global(.roll-prob),
  .preview-excerpt :global(.prob-header) {
    display: none;
  }

  .preview-path {
    font-size: 0.75rem;
    color: var(--color-text-muted, #666);
  }

  /* Floating infobox beside the preview popover */
  .preview-infobox-float {
    position: fixed;
    z-index: 1000;
    max-width: 220px;
    background: var(--color-surface, #fff);
    color: var(--color-text, #333);
    border: 1px solid var(--color-border, #e0e0e0);
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    padding: 0.5rem;
    opacity: 1;
    pointer-events: auto;
  }

  .preview-infobox-float :global(.infobox) {
    float: none;
    max-width: 100%;
    margin: 0;
    border: none;
    box-shadow: none;
    background: transparent;
    font-size: 0.8rem;
  }

  .preview-infobox-float :global(.infobox-title) {
    font-size: 0.85rem;
  }

  .preview-infobox-float :global(.infobox-table) {
    font-size: 0.75rem;
  }
</style>
